import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import {
  createResponseAICoding,
  deleteResponseAICoding,
  fetchCompletedQuestionResponses,
  fetchResponseAICoding,
  fetchResponseAICodingsForQuestionCategory,
  fetchResponseAICodingsByRespondent,
} from '../../services/backend/responseAICodings';
import { ReactQueryFunctionCallbacks } from '../../types/internal';

export const responseAICodingQueries = {
  all: () => ['responseAICodings'],
  detail: (id: number) =>
    queryOptions({
      queryFn: () => fetchResponseAICoding({ id }),
      queryKey: [...responseAICodingQueries.all(), 'detail', id],
    }),
  byQuestionCategory: (questionCategoryId: number) =>
    queryOptions({
      queryFn: () =>
        fetchResponseAICodingsForQuestionCategory({ questionCategoryId }),
      queryKey: [
        ...responseAICodingQueries.all(),
        'questionCategory',
        questionCategoryId,
      ],
    }),
  byRespondent: (opts: { questionId: number; respondentId: number }) =>
    queryOptions({
      queryFn: () =>
        fetchResponseAICodingsByRespondent({
          questionId: opts.questionId,
          respondentId: opts.respondentId,
        }),
      queryKey: [
        ...responseAICodingQueries.all(),
        'respondent',
        opts.respondentId,
        'question',
        opts.questionId,
      ],
    }),
  completedQuestionResponses: (opts: { questionId: number; waveId?: number }) =>
    queryOptions({
      queryFn: () =>
        fetchCompletedQuestionResponses({
          questionId: opts.questionId,
          waveId: opts.waveId,
        }),
      queryKey: [
        ...responseAICodingQueries.all(),
        'completedQuestionResponses',
        opts.questionId,
        opts.waveId,
      ],
    }),
};

export function useCreateResponseAICoding(
  opts?: ReactQueryFunctionCallbacks<typeof createResponseAICoding>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: createResponseAICoding,
    onSuccess: async (data, variables, context) => {
      try {
        opts?.onSuccess?.(data, variables, context);
      } catch (error) {
        console.error('Error in custom onSuccess callback:', error);
      }

      await queryClient.invalidateQueries({
        queryKey: [
          ...responseAICodingQueries.all(),
          'questionCategory',
          variables.questionCategoryId,
        ],
      });

      await queryClient.invalidateQueries({
        queryKey: [
          ...responseAICodingQueries.all(),
          'completedQuestionResponses',
        ],
      });
    },
  });
}

export function useDeleteResponseAICoding(
  opts?: ReactQueryFunctionCallbacks<typeof deleteResponseAICoding>,
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...opts,
    mutationFn: deleteResponseAICoding,
    onSuccess: async (data, variables, context) => {
      try {
        opts?.onSuccess?.(data, variables, context);
      } catch (error) {
        console.error('Error in custom onSuccess callback:', error);
      }

      await queryClient.invalidateQueries({
        queryKey: responseAICodingQueries.all(),
      });
    },
  });
}
